// src/components/PersonalInfo.js
import React from 'react';
import { User, MapPin, Shield, AlertCircle, Flag, CheckSquare } from 'react-feather';

function PersonalInfo({ formData, onDataChange }) {
  
  // Helper for nested state (e.g., presidentialCampaign.you)
  const handleNestedChange = (parent, field, value) => {
    const syntheticEvent = {
      target: {
        name: `${parent}.${field}`,
        value: value,
        type: 'checkbox',
        checked: value
      }
    };
    onDataChange(syntheticEvent);
  };

  return (
    <div className="form-section">
      <div className="content-box-header">
        <h2><User size={20} className="icon-orange" /> Personal Information</h2>
      </div>

      {/* --- PRIVACY DISCLAIMER --- */}
      <div className="privacy-notice" style={{ 
        backgroundColor: '#f0fdf4', 
        border: '1px solid #86efac', 
        borderRadius: '0.5rem', 
        padding: '1rem', 
        marginBottom: '1.5rem',
        display: 'flex',
        gap: '1rem',
        alignItems: 'flex-start'
      }}>
        <Shield size={24} color="#16a34a" style={{ flexShrink: 0 }} />
        <div>
          <h4 style={{ margin: '0 0 0.25rem 0', color: '#14532d' }}>Your Data is Private</h4>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#166534', lineHeight: '1.4' }}>
            This application runs entirely in your browser. We <strong>do not</strong> save, transmit, or see your personal information. All data is stored locally on your device.
          </p>
        </div>
      </div>

      <div className="dependent-card">
        
        {/* --- 1. FISCAL YEAR FILER (2025 Header) --- */}
        <div style={{ marginBottom: '1.5rem', borderBottom: '1px dashed #e2e8f0', paddingBottom: '1rem' }}>
            <div className="form-group checkbox-group">
                <input 
                    type="checkbox" 
                    id="isFiscalYearFiler" 
                    name="isFiscalYearFiler" 
                    checked={formData.isFiscalYearFiler || false} 
                    onChange={onDataChange} 
                />
                <label htmlFor="isFiscalYearFiler" style={{ fontWeight: '600', color: '#554333ff' }}>
                    Fiscal Year Filer (Tax year other than Jan 1 - Dec 31)
                </label>
            </div>

            {formData.isFiscalYearFiler && (
                <div className="form-row" style={{ marginTop: '0.5rem', marginLeft: '1.8rem' }}>
                    <div className="form-group">
                        <label>Beginning (2025)</label>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <input type="text" name="fiscalYearStartMonth" placeholder="MM" value={formData.fiscalYearStartMonth || ''} onChange={onDataChange} style={{ width: '60px' }} />
                            <input type="text" name="fiscalYearStartDay" placeholder="DD" value={formData.fiscalYearStartDay || ''} onChange={onDataChange} style={{ width: '50px' }} />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Ending (20__)</label>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <input type="text" name="fiscalYearEndMonth" placeholder="MM" value={formData.fiscalYearEndMonth || ''} onChange={onDataChange} style={{ width: '60px' }} />
                            <input type="text" name="fiscalYearEndDay" placeholder="DD" value={formData.fiscalYearEndDay || ''} onChange={onDataChange} style={{ width: '50px' }} />
                            <input type="text" name="fiscalYearEndYear" placeholder="YY" value={formData.fiscalYearEndYear || ''} onChange={onDataChange} style={{ width: '60px' }} />
                        </div>
                    </div>
                </div>
            )}
        </div>

        {/* --- 2. SPECIAL PROCESSING (2025 Header) --- */}
        <div style={{ marginBottom: '1.5rem', borderBottom: '1px dashed #e2e8f0', paddingBottom: '1rem' }}>
            <h4 style={{ fontSize: '0.95rem', color: '#9a3412', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Flag size={16} /> Special Processing
            </h4>
            <div className="form-row">
                <div className="form-group checkbox-group">
                    <input type="checkbox" id="filedPursuantTo301" name="filedPursuantTo301" checked={formData.filedPursuantTo301 || false} onChange={onDataChange} />
                    <label htmlFor="filedPursuantTo301">Filed pursuant to section 301.9100-2</label>
                </div>
                <div className="form-group checkbox-group">
                    <input type="checkbox" id="inCombatZone" name="inCombatZone" checked={formData.inCombatZone || false} onChange={onDataChange} />
                    <label htmlFor="inCombatZone">Combat Zone</label>
                </div>
                {/* Only show description input if checkbox is checked */}
                {formData.inCombatZone && (
                    <div className="form-group">
                        <input 
                            type="text" 
                            name="combatZoneDescription" 
                            value={formData.combatZoneDescription || ''} 
                            onChange={onDataChange} 
                            placeholder="Name of Zone (e.g. Persian Gulf)" 
                        />
                    </div>
                )}
            </div>
            
            {/* Deceased Taxpayer */}
            <div className="form-row" style={{ marginTop: '0.5rem' }}>
                <div className="form-group checkbox-group">
                    <input type="checkbox" id="isDeceased" name="isDeceased" checked={formData.isDeceased || false} onChange={onDataChange} />
                    <label htmlFor="isDeceased">Taxpayer Deceased</label>
                </div>
                {formData.isDeceased && (
                    <div className="form-group">
                        <label>Date of Death</label>
                        <input type="date" name="dateOfDeath" value={formData.dateOfDeath || ''} onChange={onDataChange} />
                    </div>
                )}
            </div>

            {/* Deceased Spouse */}
            {formData.filingStatus.includes('Married') && (
                <div className="form-row" style={{ marginTop: '0.5rem' }}>
                    <div className="form-group checkbox-group">
                        <input type="checkbox" id="isSpouseDeceased" name="isSpouseDeceased" checked={formData.isSpouseDeceased || false} onChange={onDataChange} />
                        <label htmlFor="isSpouseDeceased">Spouse Deceased</label>
                    </div>
                    {formData.isSpouseDeceased && (
                        <div className="form-group">
                            <label>Spouse Date of Death</label>
                            <input type="date" name="spouseDateOfDeath" value={formData.spouseDateOfDeath || ''} onChange={onDataChange} />
                        </div>
                    )}
                </div>
            )}
        </div>

        {/* --- 3. PERSONAL DETAILS --- */}
        <h4 style={{ marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>Taxpayer Details</h4>
        <div className="form-row">
          <div className="form-group">
            <label>First Name</label>
            <input type="text" name="firstName" value={formData.firstName || ''} onChange={onDataChange} placeholder="Jane" />
          </div>
          <div className="form-group">
            <label>Last Name</label>
            <input type="text" name="lastName" value={formData.lastName || ''} onChange={onDataChange} placeholder="Doe" />
          </div>
        </div>

        {/* Skip SSN Toggle */}
        <div className="form-group checkbox-group" style={{ marginBottom: '15px', marginTop: '1rem', backgroundColor: '#fff7ed', padding: '10px', borderRadius: '0.5rem', border: '1px solid #fed7aa' }}>
          <input type="checkbox" id="skipSSN" name="skipSSN" checked={formData.skipSSN || false} onChange={onDataChange} />
          <label htmlFor="skipSSN" style={{ color: '#9a3412', fontWeight: '600' }}>I prefer to fill in SSNs manually on the PDF.</label>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Social Security Number</label>
            {formData.skipSSN ? (
              <input type="text" disabled value="Will be left blank on PDF" style={{ backgroundColor: '#f6f5f3ff', color: '#afa59cff', fontStyle: 'italic' }} />
            ) : (
              <input type="text" name="ssn" value={formData.ssn || ''} onChange={onDataChange} placeholder="XXX-XX-XXXX" />
            )}
          </div>
          <div className="form-group">
            <label>Occupation</label>
            <input type="text" name="signatures.primaryOccupation" value={formData.signatures?.primaryOccupation || ''} onChange={onDataChange} />
          </div>
        </div>

        {/* Presidential Campaign */}
        <div className="form-group" style={{ marginTop: '1rem', backgroundColor: '#fcfaf8ff', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            <label style={{ fontSize: '0.9rem', fontWeight: '600' }}>Presidential Election Campaign ($3) - Optional</label>
           
            <div className="form-row" style={{ marginTop: '5px' }}>
                <div className="checkbox-group">
                    <input type="checkbox" id="campYou" checked={formData.presidentialCampaign?.you || false} onChange={(e) => handleNestedChange('presidentialCampaign', 'you', e.target.checked)} />
                    <label htmlFor="campYou">You</label>
                </div>
                {formData.filingStatus.includes('Married') && (
                    <div className="checkbox-group">
                        <input type="checkbox" id="campSpouse" checked={formData.presidentialCampaign?.spouse || false} onChange={(e) => handleNestedChange('presidentialCampaign', 'spouse', e.target.checked)} />
                        <label htmlFor="campSpouse">Spouse</label>
                    </div>
                )}
            </div>
        </div>

        {/* Spouse Details */}
        {formData.filingStatus.includes('Married') && (
          <>
            <h4 style={{ marginTop: '1.5rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>Spouse Details</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Spouse First Name</label>
                {/* CHANGED name="spouseName" to name="spouseFirstName" */}
                <input type="text" name="spouseFirstName" value={formData.spouseFirstName || ''} onChange={onDataChange} />
              </div>
              <div className="form-group">
                <label>Spouse Last Name</label>
                <input type="text" name="spouseLastName" value={formData.spouseLastName || ''} onChange={onDataChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Spouse SSN</label>
                {formData.skipSSN ? (
                  <input type="text" disabled value="Will be left blank on PDF" style={{ backgroundColor: '#f6f5f3ff', color: '#afa59cff', fontStyle: 'italic' }} />
                ) : (
                  <input type="text" name="spouseSSN" value={formData.spouseSSN || ''} onChange={onDataChange} placeholder="XXX-XX-XXXX" />
                )}
              </div>
              <div className="form-group">
                <label>Spouse Occupation</label>
                <input type="text" name="signatures.spouseOccupation" value={formData.signatures?.spouseOccupation || ''} onChange={onDataChange} />
              </div>
            </div>
          </>
        )}

        {/* --- 4. ADDRESS & RESIDENCY --- */}
        <h4 style={{ marginTop: '1.5rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
          <MapPin size={16} className="icon-orange" style={{ marginRight: '8px' }} />
          Home Address
        </h4>
        
        <div className="form-group checkbox-group" style={{ marginBottom: '1rem' }}>
          <input type="checkbox" id="isForeignAddress" name="isForeignAddress" checked={formData.isForeignAddress || false} onChange={onDataChange} />
          <label htmlFor="isForeignAddress">I have a foreign address (outside the U.S.)</label>
        </div>
        
        <div className="form-group">
          <label>Street Address</label>
          <input type="text" name="address" value={formData.address || ''} onChange={onDataChange} placeholder="123 Main St" />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Apt / Unit</label>
            <input type="text" name="apt" value={formData.apt || ''} onChange={onDataChange} />
          </div>
          <div className="form-group">
            <label>City</label>
            <input type="text" name="city" value={formData.city || ''} onChange={onDataChange} />
          </div>
        </div>

        {!formData.isForeignAddress ? (
          <div className="form-row">
            <div className="form-group">
              <label>State</label>
              <input type="text" name="state" value={formData.state || ''} onChange={onDataChange} maxLength={2} placeholder="CA" />
            </div>
            <div className="form-group">
              <label>Zip Code</label>
              <input type="text" name="zip" value={formData.zip || ''} onChange={onDataChange} />
            </div>
          </div>
        ) : (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Foreign Country</label>
                <input type="text" name="foreignCountry" value={formData.foreignCountry || ''} onChange={onDataChange} placeholder="e.g., Canada" />
              </div>
              <div className="form-group">
                <label>Foreign Province/State</label>
                <input type="text" name="foreignProvince" value={formData.foreignProvince || ''} onChange={onDataChange} placeholder="e.g., Ontario" />
              </div>
            </div>
            <div className="form-group">
              <label>Foreign Postal Code</label>
              <input type="text" name="foreignPostalCode" value={formData.foreignPostalCode || ''} onChange={onDataChange} placeholder="e.g., K1A 0B1" />
            </div>
          </>
        )}

        {/* Residency Checkbox */}
        <div className="form-group checkbox-group" style={{ marginTop: '1rem', backgroundColor: '#f0fdf4', padding: '10px', borderRadius: '6px', border: '1px solid #86efac' }}>
            <input type="checkbox" id="mainHomeInUS" name="mainHomeInUS" checked={formData.mainHomeInUS || false} onChange={onDataChange} />
            <label htmlFor="mainHomeInUS" style={{ color: '#166534' }}>
                <strong>2025 Residency:</strong> Check if your main home (and spouse's if filing jointly) was in the U.S. for more than half of 2025.
            </label>
        </div>

      </div>
    </div>
  );
}

export default PersonalInfo;